import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, resolveAssetUrl } from "@/lib/api";
import type { BioArtContestPoster } from "@/lib/types";
import ScrollReveal from "@/components/base/ScrollReveal";
import ImageUploadField from "@/components/feature/ImageUploadField";

const YEARS = [2021, 2020, 2019, 2018, 2017, 2016, 2015, 2014, 2013] as const;

const emptyPosterForm = {
  year: 2021 as number,
  imageUrl: "",
  description: "",
  status: "active" as string,
};

type PosterMode = "list" | "create" | "edit";

type ContestPostersPanelProps = {
  onToast: (msg: string) => void;
};

export default function ContestPostersPanel({ onToast }: ContestPostersPanelProps) {
  const [posters, setPosters] = useState<BioArtContestPoster[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<PosterMode>("list");
  const [form, setForm] = useState({ ...emptyPosterForm });
  const [editId, setEditId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const registeredYears = new Set(posters.map((p) => p.year));

  const fetchPosters = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<BioArtContestPoster[]>("/admin/bio-art/posters");
      if (res.data) setPosters(res.data);
    } catch (err: unknown) {
      onToast(err instanceof Error ? err.message : "포스터 로딩 실패");
      setPosters([]);
    } finally {
      setLoading(false);
    }
  }, [onToast]);

  useEffect(() => {
    if (mode === "list") {
      void fetchPosters();
    }
  }, [mode, fetchPosters]);

  const handleSave = async () => {
    if (!form.imageUrl.trim()) {
      onToast("포스터 이미지를 등록해주세요.");
      return;
    }

    const payload = {
      year: form.year,
      imageUrl: form.imageUrl.trim(),
      description: form.description.trim() || null,
      status: form.status,
    };

    setSaving(true);
    try {
      if (mode === "edit" && editId) {
        await api.put(`/admin/bio-art/posters/${editId}`, payload);
        onToast("포스터가 수정되었습니다.");
      } else {
        await api.post("/admin/bio-art/posters", payload);
        onToast("포스터가 등록되었습니다.");
      }
      setMode("list");
      setForm({ ...emptyPosterForm });
      setEditId(null);
    } catch (err: unknown) {
      onToast(err instanceof Error ? err.message : "저장 실패");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (poster: BioArtContestPoster) => {
    setEditId(poster.id);
    setForm({
      year: poster.year,
      imageUrl: poster.imageUrl,
      description: poster.description ?? "",
      status: poster.status,
    });
    setMode("edit");
  };

  const handleDelete = async (id: number) => {
    if (!confirm("정말 삭제하시겠습니까?")) return;
    try {
      await api.delete(`/admin/bio-art/posters/${id}`);
      onToast("삭제되었습니다.");
      await fetchPosters();
    } catch (err: unknown) {
      onToast(err instanceof Error ? err.message : "삭제 실패");
    }
  };

  if (mode === "create" || mode === "edit") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <div className="max-w-2xl mx-auto bg-white rounded-xl border border-gray-200 p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-[#2c4964]">
              {mode === "create" ? "Add Contest Poster" : "Edit Contest Poster"}
            </h2>
            <button
              type="button"
              onClick={() => {
                setMode("list");
                setForm({ ...emptyPosterForm });
                setEditId(null);
              }}
              className="text-sm text-gray-500 hover:text-[#1977cc] transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>

          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-[#2c4964] mb-1.5">
                  Year <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.year}
                  onChange={(e) => setForm((f) => ({ ...f, year: Number(e.target.value) }))}
                  disabled={mode === "edit"}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] bg-white disabled:bg-gray-50 disabled:text-gray-500"
                >
                  {YEARS.map((y) => (
                    <option
                      key={y}
                      value={y}
                      disabled={mode === "create" && registeredYears.has(y) && form.year !== y}
                    >
                      {y}
                      {mode === "create" && registeredYears.has(y) ? " (등록됨)" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] bg-white"
                >
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                  <option value="draft">draft</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2c4964] mb-1.5">
                Description <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] resize-none"
                placeholder="일반 화면 포스터 카드에 표시할 설명 (선택)"
              />
              <p className="mt-1 text-xs text-gray-400 text-right">{form.description.length}/500</p>
            </div>

            <ImageUploadField
              label="Contest Poster"
              value={form.imageUrl}
              onChange={(url) => setForm((f) => ({ ...f, imageUrl: url }))}
            />

            {form.imageUrl && (
              <div className="rounded-lg border border-gray-200 bg-[#f6f9fc] p-4">
                <p className="text-xs font-medium text-gray-400 mb-3 uppercase tracking-wide">Preview</p>
                <div className="flex justify-center">
                  <img
                    src={resolveAssetUrl(form.imageUrl)}
                    alt={`${form.year} contest poster preview`}
                    className="max-h-80 w-auto max-w-[200px] object-contain rounded-md shadow-sm"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setMode("list");
                  setForm({ ...emptyPosterForm });
                  setEditId(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 bg-[#1977cc] text-white text-sm font-medium rounded-md hover:bg-[#1669b3] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {mode === "create" ? "Create" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <>
      <ScrollReveal>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <p className="text-sm text-gray-500">
            연도별 Bio-Art Contest 세로형 포스터를 등록합니다. 일반 화면 Year 탭 아래에 표시됩니다.
          </p>
          <button
            type="button"
            onClick={() => {
              const nextYear = YEARS.find((y) => !registeredYears.has(y)) ?? 2021;
              setForm({ ...emptyPosterForm, year: nextYear });
              setEditId(null);
              setMode("create");
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#1977cc] text-white text-sm font-medium rounded-md hover:bg-[#1669b3] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" /> Add Poster
          </button>
        </div>
      </ScrollReveal>

      <ScrollReveal delay={0.05}>
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#f1f7fc]">
                <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-16">ID</th>
                <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-28">Year</th>
                <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-36">Preview</th>
                <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200">Status</th>
                <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-40">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
              ) : posters.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">등록된 포스터가 없습니다.</td></tr>
              ) : (
                posters.map((poster) => (
                  <tr key={poster.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 border-b border-gray-100 text-gray-500">{poster.id}</td>
                    <td className="px-4 py-3 border-b border-gray-100 text-gray-700 font-medium">{poster.year}</td>
                    <td className="px-4 py-3 border-b border-gray-100">
                      <div className="w-16 h-24 bg-[#f6f9fc] rounded border border-gray-100 flex items-center justify-center overflow-hidden">
                        <img
                          src={resolveAssetUrl(poster.imageUrl)}
                          alt={`${poster.year} poster`}
                          className="max-h-full max-w-full object-contain"
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 border-b border-gray-100">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        poster.status === "active" ? "bg-green-50 text-green-700"
                        : poster.status === "inactive" ? "bg-red-50 text-red-700"
                        : "bg-yellow-50 text-yellow-700"
                      }`}>{poster.status}</span>
                    </td>
                    <td className="px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(poster)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-[#1977cc] hover:bg-[#f1f7fc] rounded transition-colors cursor-pointer"
                        >
                          <i className="ri-edit-line" /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(poster.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </ScrollReveal>
    </>
  );
}
